import { makeObservable, observable, flow, computed, action, runInAction } from "mobx";

import { api } from "@/lib/services";
import { ApiRoutes } from "@/lib/routes";
import { RiskMatrixVariants } from "@/areas/risks/components/reviews/RiskMatrixVariants";

const STORAGE_KEY = "risk-matrix-config";

const DEFAULT_LABELS = {
  critical: "Critical",
  major: "Major",
  moderate: "Moderate",
  minor: "Minor",
  negligible: "Negligible",
};

const DEFAULT_CONFIG = {
  version: 1,
  dimension: 5,
  invertAxes: false,
  preset: "5x5-alt1",
  isCustom: false,
  matrix: RiskMatrixVariants["5x5-alt1"].matrix,
};

export default class RiskMatrixConfigurationStore {
  loading = false;
  saving = false;
  config = null;
  pendingConfig = null;
  impactPreview = null;
  pendingLabels = null;
  _typeConfigCache = observable.map();
  _pendingLoads = new Map();

  constructor(parentStore) {
    makeObservable(this, {
      loading: observable,
      saving: observable,
      config: observable,
      pendingConfig: observable,
      impactPreview: observable,
      pendingLabels: observable,
      isCustom: computed,
      isDirty: computed,
      isLabelsDirty: computed,
      changedCells: computed,
      load: flow,
      loadConfig: flow,
      loadImpactPreview: flow,
      saveCellScores: flow,
      resetToShared: flow,
      setCellScore: action,
      setLabel: action,
      setPreset: action,
      toggleInvertAxes: action,
      resetChanges: action,
    });

    this.parentStore = parentStore;
  }

  get isCustom() {
    return this.config?.isCustom ?? false;
  }

  get isDirty() {
    if (!this.config || !this.pendingConfig) return false;
    return (
      this.config.preset !== this.pendingConfig.preset ||
      JSON.stringify(this.config.matrix) !== JSON.stringify(this.pendingConfig.matrix) ||
      this.isLabelsDirty
    );
  }

  get isLabelsDirty() {
    if (!this.config || !this.pendingLabels) return false;
    const savedLabels = this.config.labels ?? DEFAULT_LABELS;
    return JSON.stringify(savedLabels) !== JSON.stringify(this.pendingLabels);
  }

  get changedCells() {
    if (!this.config || !this.pendingConfig) return [];
    const changed = [];
    for (let ri = 0; ri < this.config.matrix.length; ri++) {
      for (let ci = 0; ci < this.config.matrix[ri].length; ci++) {
        const orig = this.config.matrix[ri][ci];
        const pending = this.pendingConfig.matrix[ri][ci];
        if (orig.score !== pending.score) {
          changed.push(pending);
        }
      }
    }
    return changed;
  }

  _resolveLabels(labels) {
    if (labels) return labels;
    // Fall back to shared config labels, then hardcoded defaults
    const shared = this._typeConfigCache.get("shared");
    return shared?.labels ?? DEFAULT_LABELS;
  }

  *loadConfig(riskTypeId) {
    const cacheKey = riskTypeId || "shared";

    // Check in-memory cache first
    if (this._typeConfigCache.has(cacheKey)) {
      return this._typeConfigCache.get(cacheKey);
    }

    // Deduplicate concurrent requests for the same key
    if (this._pendingLoads.has(cacheKey)) {
      return yield this._pendingLoads.get(cacheKey);
    }

    const promise = this._doLoadConfig(riskTypeId, cacheKey);
    this._pendingLoads.set(cacheKey, promise);
    try {
      return yield promise;
    } finally {
      this._pendingLoads.delete(cacheKey);
    }
  }

  async _doLoadConfig(riskTypeId, cacheKey) {
    // Check localStorage
    const storageKey = `risk-matrix-config-${cacheKey}`;
    const cached = localStorage.getItem(storageKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const result = {
          matrix: parsed.matrix,
          labels: this._resolveLabels(parsed.labels),
        };
        runInAction(() => this._typeConfigCache.set(cacheKey, result));
        return result;
      } catch {
        // ignore corrupt cache
      }
    }

    // Fetch from API
    try {
      const response = await api.get(ApiRoutes.forRiskMatrixConfiguration(riskTypeId), {
        headers: { "Cache-Control": "no-cache" },
      });
      const data = response.data;
      const result = {
        matrix: data.matrix,
        labels: this._resolveLabels(data.labels),
      };
      runInAction(() => {
        this._typeConfigCache.set(cacheKey, result);
      });
      localStorage.setItem(storageKey, JSON.stringify(data));
      return result;
    } catch {
      // Fall back to defaults
      return {
        matrix: DEFAULT_CONFIG.matrix,
        labels: this._resolveLabels(null),
      };
    }
  }

  getConfig(riskTypeId) {
    const cacheKey = riskTypeId || "shared";
    return this._typeConfigCache.get(cacheKey) ?? null;
  }

  *load(riskTypeId) {
    // Try localStorage first for instant render
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached && !this.config) {
      try {
        this.config = JSON.parse(cached);
        this.pendingConfig = JSON.parse(cached);
      } catch {
        // ignore corrupt cache
      }
    }

    try {
      this.loading = true;
      const response = yield api.get(ApiRoutes.forRiskMatrixConfiguration(riskTypeId), {
        headers: { "Cache-Control": "no-cache" },
      });
      const data = response.data;
      this.config = data;
      this.pendingConfig = JSON.parse(JSON.stringify(data));
      this.pendingLabels = { ...(data.labels ?? DEFAULT_LABELS) };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

      // Warm the type config cache
      const cacheKey = riskTypeId || "shared";
      this._typeConfigCache.set(cacheKey, {
        matrix: data.matrix,
        labels: this._resolveLabels(data.labels),
      });

      return { success: true, data };
    } catch (e) {
      // Fall back to default if API unavailable and no cached config
      if (!this.config) {
        this.config = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
        this.pendingConfig = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
        this.pendingLabels = { ...DEFAULT_LABELS };
      }
      return { success: false, error: e };
    } finally {
      this.loading = false;
    }
  }

  *loadImpactPreview(riskTypeId) {
    try {
      const response = yield api.post(ApiRoutes.forRiskMatrixImpactPreview(), {
        riskTypeId: riskTypeId || null,
        changedCells: this.changedCells,
      });
      this.impactPreview = response.data;
      return { success: true, data: response.data };
    } catch (e) {
      return { success: false, error: e };
    }
  }

  *saveCellScores(riskTypeId, updateExistingRisks) {
    try {
      this.saving = true;
      const response = yield api.post(ApiRoutes.forRiskMatrixCellScores(), {
        riskTypeId: riskTypeId || null,
        matrix: this.pendingConfig.matrix,
        updateExistingRisks,
        labels: this.pendingLabels,
      });

      // Reload from API to get fresh state
      localStorage.removeItem(STORAGE_KEY);
      this._clearTypeConfigCache(riskTypeId);
      yield this.load(riskTypeId);
      this.impactPreview = null;

      return { success: true, data: response.data };
    } catch (e) {
      return { success: false, error: e };
    } finally {
      this.saving = false;
    }
  }

  *resetToShared(riskTypeId) {
    try {
      this.saving = true;
      yield api.delete(ApiRoutes.forResetRiskTypeCustomization(riskTypeId));

      // Reload from API to get shared config
      localStorage.removeItem(STORAGE_KEY);
      this._clearTypeConfigCache(riskTypeId);
      yield this.load(riskTypeId);
      this.impactPreview = null;

      return { success: true };
    } catch (e) {
      return { success: false, error: e };
    } finally {
      this.saving = false;
    }
  }

  _clearTypeConfigCache(riskTypeId) {
    const cacheKey = riskTypeId || "shared";
    this._typeConfigCache.delete(cacheKey);
    localStorage.removeItem(`risk-matrix-config-${cacheKey}`);
  }

  setCellScore(likelihood, impact, score) {
    if (!this.pendingConfig) return;
    for (const row of this.pendingConfig.matrix) {
      for (const cell of row) {
        if (cell.likelihood === likelihood && cell.impact === impact) {
          cell.score = score;
          return;
        }
      }
    }
  }

  setLabel(score, text) {
    if (!this.pendingLabels) return;
    this.pendingLabels[score] = text;
  }

  setPreset(presetName, presetMatrix) {
    if (!this.pendingConfig) return;
    this.pendingConfig.preset = presetName;
    this.pendingConfig.matrix = JSON.parse(JSON.stringify(presetMatrix));
  }

  toggleInvertAxes() {
    if (!this.pendingConfig) return;
    this.pendingConfig.invertAxes = !this.pendingConfig.invertAxes;
  }

  resetChanges() {
    if (!this.config) return;
    this.pendingConfig = JSON.parse(JSON.stringify(this.config));
    this.pendingLabels = { ...(this.config.labels ?? DEFAULT_LABELS) };
    this.impactPreview = null;
  }
}
